import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, fmtErr } from "../lib/api";
import { ROLES, roleLabel } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Key, Copy, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const arr = new Uint32Array(10);
  crypto.getRandomValues(arr);
  return "Fg#" + Array.from(arr, (n) => chars[n % chars.length]).join("");
};

export default function UserAccess() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [modules, setModules] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "engineer", discipline: "", password: "", workspace_id: "", ws_modules: [] });
  const [wsByUser, setWsByUser] = useState({});
  const [creds, setCreds] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");

  const load = async () => {
    try {
      const reqs = [api.get("/users"), api.get("/org")];
      if (isAdmin) reqs.push(api.get("/workspaces"), api.get("/workspaces/meta"));
      const [u, o, w, m] = await Promise.all(reqs);
      setUsers(u.data);
      setDisciplines(o.data?.settings?.disciplines || []);
      if (w) {
        setWorkspaces(w.data);
        const map = {};
        for (const ws of w.data) for (const mem of ws.members || []) {
          (map[mem.user_id] = map[mem.user_id] || []).push({ name: ws.name, modules: mem.modules || [] });
        }
        setWsByUser(map);
      }
      if (m) setModules(m.data.modules || []);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const changeRole = async (id, role) => {
    try { await api.patch(`/users/${id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const changeDiscipline = async (id, discipline) => {
    try { await api.patch(`/users/${id}`, { discipline: discipline === "none" ? "" : discipline }); toast.success("Department updated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.workspaces = form.workspace_id ? [{ workspace_id: form.workspace_id, modules: form.ws_modules }] : [];
      delete payload.workspace_id; delete payload.ws_modules;
      const { data } = await api.post("/users", payload);
      setCreds(data);
      setShowNew(false);
      setForm({ name: "", email: "", role: "engineer", discipline: "", password: "", workspace_id: "", ws_modules: [] });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/users/${resetTarget.id}/reset-password`, { password: resetPw });
      setCreds({ ...data, name: resetTarget.name, email: resetTarget.email, reset: true });
      setResetTarget(null); setResetPw("");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const copyCreds = () => {
    const text = [
      `Formigen — Product Design account`,
      `Name: ${creds.name}`,
      `Login ID: ${creds.email}`,
      `Temporary password: ${creds.password}`,
      `Sign in: ${window.location.origin}/login`,
      `(You'll be asked to set your own password at first sign-in.)`,
    ].join("\n");
    navigator.clipboard.writeText(text).then(() => toast.success("Credentials copied"));
  };

  const selWs = useMemo(() => workspaces.find((w) => w.id === form.workspace_id), [workspaces, form.workspace_id]);

  return (
    <div data-testid="user-access-page">
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Team · {users.length} members</div>
          {isAdmin && (
            <Button data-testid="add-user-btn" onClick={() => setShowNew(true)} size="sm"
              className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
              <Plus size={13} className="mr-1" /> Create User
            </Button>
          )}
        </div>
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-[var(--c-border)]">
              {["Name", "Email", "Role", "Department", "Workspaces", "Joined", ""].map((h) => (
                <th key={h} className="text-left px-2 py-2 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[var(--c-border)]/60 last:border-0" data-testid={`user-row-${u.email}`}>
                <td className="px-2 py-2.5 text-xs font-medium text-[var(--c-text)]">{u.name}</td>
                <td className="px-2 py-2.5 text-xs text-[var(--c-muted)]">{u.email}</td>
                <td className="px-2 py-2.5">
                  {isAdmin && u.id !== user.id ? (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger data-testid={`role-select-${u.email}`} className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                        {ROLES.map((r) => <SelectItem key={r.key} value={r.key} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-xs text-[var(--c-text)]">{roleLabel(u.role)}</span>
                  )}
                </td>
                <td className="px-2 py-2.5">
                  {isAdmin ? (
                    <Select value={u.discipline || "none"} onValueChange={(v) => changeDiscipline(u.id, v)}>
                      <SelectTrigger data-testid={`discipline-select-${u.email}`} className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                        <SelectItem value="none" className="text-[var(--c-muted)] focus:bg-[var(--c-surface-2)]">— none —</SelectItem>
                        {disciplines.map((d) => <SelectItem key={d} value={d} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-xs text-[var(--c-muted)]">{u.discipline || "—"}</span>
                  )}
                </td>
                <td className="px-2 py-2.5" data-testid={`user-ws-${u.email}`}>
                  {(wsByUser[u.id] || []).length === 0 ? (
                    <span className="text-xs text-[var(--c-muted)]">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {wsByUser[u.id].map((w) => (
                        <span key={w.name} className="text-[10px] font-mono px-1.5 py-0.5 bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm text-[var(--c-muted)]">
                          {w.name} · {w.modules.length} mod
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2.5 font-mono text-xs text-[var(--c-muted)]">{String(u.created_at || "").slice(0, 10)}</td>
                <td className="px-2 py-2.5 text-right">
                  {isAdmin && u.id !== user.id && (
                    <button data-testid={`reset-pw-${u.email}`} title="Reset password & resend invite"
                      onClick={() => { setResetTarget(u); setResetPw(""); }}
                      className="text-[var(--c-muted)] hover:text-[var(--c-accent)] transition-colors duration-150">
                      <Key size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create user */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-h-[90vh] overflow-y-auto" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="text-[var(--c-text)]">Create User</DialogTitle><DialogDescription className="sr-only">Create a user, set their password, and grant workspace access</DialogDescription></DialogHeader>
          <form onSubmit={addUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Full name</Label>
                <Input data-testid="au-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Email (login ID)</Label>
                <Input data-testid="au-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="au-role" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    {ROLES.map((r) => <SelectItem key={r.key} value={r.key} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Department</Label>
                <Input data-testid="au-discipline" list="dept-options" value={form.discipline}
                  onChange={(e) => setForm({ ...form, discipline: e.target.value })}
                  placeholder="e.g. Design, Quality, SCM" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
                <datalist id="dept-options">{disciplines.map((d) => <option key={d} value={d} />)}</datalist>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Temporary password</Label>
              <div className="flex gap-2">
                <Input data-testid="au-password" required minLength={8} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Min. 8 characters" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono" />
                <Button type="button" variant="outline" size="sm" data-testid="au-generate-pw"
                  onClick={() => setForm({ ...form, password: genPassword() })}
                  className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-9 text-xs shrink-0">
                  Generate
                </Button>
              </div>
              <div className="text-[10px] text-[var(--c-muted)]">Emailed invite never contains the password — share it with the user directly. They'll set their own password at first sign-in.</div>
            </div>
            {workspaces.length > 0 && (
              <div className="space-y-2 border-t border-[var(--c-border)] pt-3">
                <Label className="text-[var(--c-muted)] text-xs">Workspace access (optional)</Label>
                <Select value={form.workspace_id || "none"} onValueChange={(v) => setForm({ ...form, workspace_id: v === "none" ? "" : v, ws_modules: v === "none" ? [] : form.ws_modules })}>
                  <SelectTrigger data-testid="au-workspace" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    <SelectItem value="none" className="text-[var(--c-muted)] focus:bg-[var(--c-surface-2)]">— No workspace —</SelectItem>
                    {workspaces.map((w) => <SelectItem key={w.id} value={w.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {selWs && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-1" data-testid="au-modules">
                    {modules.map((m) => (
                      <label key={m.key} className="flex items-center gap-2 text-xs text-[var(--c-text)] cursor-pointer">
                        <input type="checkbox" data-testid={`au-mod-${m.key}`}
                          checked={form.ws_modules.includes(m.key)}
                          onChange={(e) => setForm({ ...form, ws_modules: e.target.checked ? [...form.ws_modules, m.key] : form.ws_modules.filter((x) => x !== m.key) })}
                          className="accent-[var(--c-accent)] w-3.5 h-3.5" />
                        {m.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button data-testid="au-submit" className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
              Create User & Send Invite
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle className="text-[var(--c-text)]">Reset password — {resetTarget?.name}</DialogTitle><DialogDescription className="sr-only">Set a new temporary password</DialogDescription></DialogHeader>
          <form onSubmit={doReset} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">New temporary password</Label>
              <div className="flex gap-2">
                <Input data-testid="rp-password" required minLength={8} value={resetPw} onChange={(e) => setResetPw(e.target.value)}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono" />
                <Button type="button" variant="outline" size="sm" data-testid="rp-generate" onClick={() => setResetPw(genPassword())}
                  className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-9 text-xs shrink-0">Generate</Button>
              </div>
              <div className="text-[10px] text-[var(--c-muted)]">The user will be asked to set their own password at next sign-in. A fresh invite email is sent.</div>
            </div>
            <Button data-testid="rp-submit" className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">Reset Password</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials card */}
      <Dialog open={!!creds} onOpenChange={() => setCreds(null)}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" data-testid="creds-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)]">{creds?.reset ? "Password reset" : "User created"} — share these credentials</DialogTitle>
            <DialogDescription className="sr-only">Credentials to share with the user</DialogDescription>
          </DialogHeader>
          {creds && (
            <div className="space-y-3">
              <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-sm border ${creds.email_sent ? "border-[var(--c-accent)]/40 bg-[var(--c-accent)]/10 text-[var(--c-accent)]" : "border-[var(--c-amber,#b45309)]/40 bg-[var(--c-amber,#b45309)]/10 text-[var(--c-amber,#b45309)]"}`} data-testid="creds-email-status">
                {creds.email_sent ? <CheckCircle size={14} /> : <WarningCircle size={14} />}
                {creds.email_sent
                  ? `Invite email sent to ${creds.email} (login link + ID — never the password).`
                  : `Invite email could not be sent — share the credentials below manually.`}
              </div>
              <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-4 space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-[var(--c-muted)]">Name</span><span className="text-[var(--c-text)]" data-testid="creds-name">{creds.name}</span></div>
                <div className="flex justify-between"><span className="text-[var(--c-muted)]">Login ID</span><span className="text-[var(--c-text)] font-mono" data-testid="creds-email">{creds.email}</span></div>
                <div className="flex justify-between"><span className="text-[var(--c-muted)]">Temporary password</span><span className="text-[var(--c-text)] font-mono" data-testid="creds-password">{creds.password}</span></div>
                <div className="flex justify-between"><span className="text-[var(--c-muted)]">Sign in at</span><span className="text-[var(--c-text)] font-mono">{window.location.origin}/login</span></div>
              </div>
              <div className="text-[10px] text-[var(--c-muted)] leading-relaxed">
                The user must set their own password at first sign-in. For security, the password is never included in the email.
              </div>
              <Button data-testid="creds-copy" onClick={copyCreds} variant="outline"
                className="w-full border-[var(--c-border)] text-[var(--c-text)] hover:bg-[var(--c-bg)] rounded-sm">
                <Copy size={13} className="mr-1.5" /> Copy credentials
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
